import {
  BASE_PATH,
  parseTraceArray,
  type SnapshotEntity,
  type SnapshotFilter,
  type TraceConfig,
  type TraceEvent,
  type TraceFilter,
  type WorldSnapshot,
} from '@ai-studio/shared';
import { create } from 'zustand';

import { pageChannel, resetEngineWorker, startEngineWorker, workerChannel, workerPossible, type ViewChannel } from './engine-bridge.js';
// Its own file, never a data: URL (the CSP allows worklet scripts from 'self' only).
import ENGINE_AUDIO_WORKLET_URL from './engine-audio.worklet.js?url&no-inline';

/**
 * Typed wrapper for the Prism engine's WASM build.
 *
 * The bundle is produced by `pnpm engine:build` (wasm-bindgen --target web)
 * and served by the Prism server at `${BASE_PATH}/engine/`. It is loaded at
 * runtime with a dynamic import, never bundled, so the editor builds and runs
 * without it: when it is absent every viewport shows a placeholder and the
 * hierarchy/inspector keep editing the JSON documents directly.
 *
 * The contract is documented in ./README.md and implemented by the Rust crate
 * `prism_wasm`.
 */

/** `preview`: an isolated edit-mode stage (the Particle Editor's preview; it never replaces the Scene view). */
export type PrismMode = 'scene' | 'model' | 'play' | 'preview';
export type PrismTool = 'select' | 'move' | 'rotate' | 'scale';

export interface PrismHost {
  /** Read a project file (path relative to the project root). */
  readFile(path: string): Promise<Uint8Array>;
  /** Receives every engine event as a JSON string. */
  onEvent(json: string): void;
  /** Stores an engine cache file (`.prism/cache/...`); optional. */
  writeCache?(path: string, bytes: Uint8Array): void;
  /** Synchronous lookup of a cache file another view built this session. */
  readCache?(path: string): Uint8Array | undefined;
  /** Save slots for Play (`{slot: jsonText}`); without these the engine uses localStorage. */
  loadSaves?(): Promise<Record<string, string>>;
  writeSave?(slot: string, json: string): Promise<unknown> | void;
  deleteSave?(slot: string): Promise<unknown> | void;
  /** The Prism account of the person playing (games with `settings.account` see it). */
  account?: { id: number; username: string; token?: string } | null;
}

/** The `PrismView` instance exported by prism_wasm.js. */
export interface PrismViewApi {
  openProject(): Promise<void>;
  openScene(path: string): Promise<void>;
  openModel(path: string): Promise<void>;
  /** prism EditorCommand JSON → result JSON `{ok, error?}`. */
  command(json: string): string;
  /** prism ModelCommand JSON → result JSON `{ok, error?}`. */
  modelCommand(json: string): string;
  /** Scene view drop placement at CSS pixel (x, y) → {@link DropPoint} JSON (optional: older bundles lack it). */
  dropPoint?(x: number, y: number): string;
  setTool(tool: PrismTool): void;
  setOption(key: string, value: string): void;
  play(): Promise<void>;
  stop(): void;
  pause(paused: boolean): void;
  fileChanged(path: string): Promise<void>;
  resize(width: number, height: number, dpr: number): void;
  input?(json: string): void;
  /** Mouse acts as a finger and touch controls show (the Play tab's phone preview; newer bundles only). */
  setTouchEmulation?(on: boolean): void;
  /** Changes the Prism account of the person playing (JSON, or "" for none; newer bundles only). */
  setAccount?(json: string): void;
  /** Safe-area insets in CSS px (newer bundles only). */
  setSafeAreaInsets?(top: number, right: number, bottom: number, left: number): void;
  screenshot(): Uint8Array;
  /** PNG of the view for page and offscreen canvases (optional: older bundles lack it). */
  screenshotPng?(): Promise<Uint8Array>;
  /* Engine worker hooks (optional: older bundles lack them). */
  /** Offscreen canvas: whether the page shows the view. */
  setVisible?(visible: boolean): void;
  /** Pads read from the page's Gamepad API (JSON array of `{index, id, buttons, axes}`). */
  setGamepads?(json: string): void;
  /** Audio through the page's AudioWorkletNode port (call before `play`). */
  attachAudio?(port: MessagePort, sampleRate: number): void;
  /** Profiler command → `{ok, status}` JSON (optional: older bundles lack it; see {@link ProfilerCommand}). */
  profiler?(json: string): string;
  /** Recorded frames with seq > `since` (-1 = all) → `{frames, status}` JSON. */
  profilerFrames?(since: number, max?: number): string;
  /** Frame debugger op (see {@link FrameDebuggerCommand}) → result JSON. */
  frameDebugger?(json: string): Promise<string>;
  free(): void;
  /* Editor play tools (optional: older bundles lack them). */
  /** While paused: advance one frame; false when not paused. */
  step?(): boolean;
  /** Editor play speed (0..16). */
  setPlaySpeed?(speed: number): void;
  /** Every entity of the running world ({@link LiveTree} JSON). */
  liveTree?(): string;
  /** One entity with all components ({@link LiveEntity} JSON or "null"). */
  liveEntity?(id: string): string;
  /** Edits the running world (`{op, id, ...}`) → `{ok, error?}`. */
  liveEdit?(json: string): string;
  /** Loads these scenes (JSON array of paths) next to the open one. */
  setAdditiveScenes?(json: string): Promise<void>;
  /** Sequence director command ({@link SequenceCommand} JSON) → state JSON (optional: older bundles lack it). */
  sequenceCommand?(json: string): Promise<string>;
  /** Sequence director state JSON ({@link SequenceState}). */
  sequenceState?(): string;
  /* Game trace: the Play window's debug console (optional: older bundles lack it). */
  /** What to trace ({@link TraceConfig} JSON). */
  traceConfig?(json: string): void;
  /** Events with seq > `sinceSeq` matching the {@link TraceFilter} JSON → JSON array of {@link TraceEvent}. */
  traceTake?(sinceSeq: number, filterJson: string): string;
  /** Running world ({@link SnapshotFilter} JSON) → `{entities: [...]}` JSON ({@link WorldSnapshot}). */
  worldSnapshot?(filterJson: string): string;
  /** One running entity with every component ({@link SnapshotEntity} JSON or "null"). */
  entityState?(id: string): string;
}

/** Commands of the editor's sequence director (engine/docs/systems/sequences.md "Editor hooks"). */
export type SequenceCommand =
  | { cmd: 'load'; path: string }
  | { cmd: 'unload' }
  | { cmd: 'seek'; time: number }
  | { cmd: 'play' }
  | { cmd: 'pause' }
  | { cmd: 'stop' }
  | { cmd: 'loop'; on: boolean }
  | { cmd: 'previewCamera'; on: boolean }
  | { cmd: 'state' };

/** The director's state (`view.sequenceState()`). */
export interface SequenceState {
  loaded: boolean;
  path?: string | null;
  name?: string;
  time: number;
  duration: number;
  fps?: number;
  playing: boolean;
  loop?: boolean;
  camera?: string | null;
  subtitles?: { speaker?: string; text: string }[];
  fade?: number;
  previewCamera?: boolean;
  sequencers?: unknown[];
}

/** Entity of the running world (`PrismView.liveTree`). */
export interface LiveTreeEntity {
  id: string;
  name: string;
  parent: string | null;
  /** Component keys. */
  components: string[];
  /** Created by a prefab, stream or additive scene (not in the open scene). */
  spawned?: boolean;
  /** Additive scene path. */
  scene?: string;
}

export interface LiveTree {
  mode: 'edit' | 'play' | 'paused';
  frame: number;
  time: number;
  speed: number;
  entities: LiveTreeEntity[];
}

/** One running entity (`PrismView.liveEntity`). */
export interface LiveEntity {
  id: string;
  name: string;
  parent: string | null;
  transform: { position: [number, number, number]; rotation: [number, number, number]; scale: [number, number, number] };
  components: Record<string, unknown>;
}

export type LiveEdit =
  | { op: 'setTransform'; id: string; transform: LiveEntity['transform'] }
  | { op: 'setComponent'; id: string; key: string; value: unknown }
  | { op: 'removeComponent'; id: string; key: string }
  | { op: 'rename'; id: string; name: string }
  | { op: 'destroy'; id: string };

/* Profiler / Frame Debugger contract (prism_wasm/src/profiler.rs). */

export interface ProfilerCommand {
  record?: boolean;
  clear?: boolean;
  /** Frames the engine keeps. */
  capacity?: number;
  /** GPU pass timings (timestamp queries) while recording. */
  gpu?: boolean;
}

export interface ProfilerStatus {
  recording: boolean;
  capacity: number;
  frames: number;
  nextSeq: number;
  gpu: { supported: boolean; enabled: boolean };
}

/** `profilerFrames` result; frames are parsed by `lib/profiler-model.ts` (`parseFrame`). */
export interface ProfilerFramesResult {
  frames: unknown[];
  status: ProfilerStatus | null;
}

export type FrameDebuggerCommand =
  | { op: 'enable' | 'capture' | 'disable' | 'status' }
  | {
      op: 'event';
      index: number;
      view?: 'color' | 'depth';
      /** Shown channels, e.g. `rgb`, `a`. */
      channels?: string;
      range?: [number, number];
      maxSize?: number;
    };

/** One event of a captured frame. */
export interface FrameDebuggerEvent {
  index: number;
  kind: 'pass' | 'draw';
  name: string;
  /** Index of the enclosing pass event. */
  pass: number;
  camera: number;
  group?: string;
  target?: { width: number; height: number; format: string; color: boolean; depth: boolean };
  vertices?: number;
  indices?: number;
  instances?: number;
  mesh?: { handle: string; name: string | null };
  material?: { handle: string; name: string | null };
  shader?: string;
  pipeline?: string;
  blend?: string;
  cull?: string;
  params?: Record<string, string>;
  entities?: { id: number; stableId?: string | null; name?: string | null; part?: string | null }[];
}

export interface FrameDebuggerCapture {
  ok: boolean;
  error?: string;
  active?: boolean;
  /** Play mode was paused by the debugger. */
  paused?: boolean;
  size?: [number, number];
  events?: FrameDebuggerEvent[];
}

export interface FrameDebuggerPreview {
  ok: boolean;
  error?: string;
  width?: number;
  height?: number;
  pass?: string;
  kind?: 'color' | 'depth';
  source?: { width: number; height: number; format: string; color: boolean; depth: boolean };
  /** Base64 RGBA8, top row first. */
  rgba?: string;
}

/** The engine's UI document layout (`UiLayout` exported by prism_wasm.js). */
export interface UiLayoutApi {
  /** Registers a TTF/OTF font under its project path (as style sheets name it). */
  addFont(path: string, bytes: Uint8Array): boolean;
  /** Replaces the default font (`settings.ui.defaultFont`). */
  setDefaultFont(bytes: Uint8Array): boolean;
  /**
   * Lays out a `*.ui.json` (text) with style sheets (JSON array, after the
   * built-in sheet) and `settings.ui` (JSON) on a `width`×`height` px
   * screen. Returns {@link UiLayoutResult} JSON or `{"error": "..."}`.
   */
  layout(doc: string, sheets: string, settingsUi: string, width: number, height: number): string;
  free(): void;
}

/** Result of {@link UiLayoutApi.layout}: rects in canvas units, y up. */
export interface UiLayoutResult {
  scale: number;
  canvas: [number, number];
  elements: {
    path: string;
    rect: [number, number, number, number];
    clip: [number, number, number, number] | null;
    visible: boolean;
    /** Visual transform `[a, b, c, d, e, f]` (rotation, style scale / translate / rotate; canvas units, y up), absent = none. */
    xform?: [number, number, number, number, number, number] | null;
  }[];
}

/**
 * Measurements of a clip (`prism_sound::analysis::Analysis`, same JSON as
 * `prism sound analyze --json`). dB values are floored at -120 (silence).
 */
export interface SoundAnalysis {
  duration: number;
  sampleRate: number;
  /** 1 when both channels are identical. */
  channels: number;
  peakDbfs: number;
  truePeakDbtp: number;
  rmsDb: number;
  lufs: number | null;
  shortTermMaxLufs: number | null;
  momentaryMaxLufs: number | null;
  loudnessRange: number | null;
  crestFactorDb: number;
  dcOffset: number;
  clippedSamples: number;
  silenceHead: number;
  silenceTail: number;
  onsets: number[];
  tempoBpm: number | null;
  tempoConfidence: number | null;
  pitch: {
    medianHz: number;
    midi: number;
    note: string;
    cents: number;
    minHz: number;
    maxHz: number;
    voicedRatio: number;
  } | null;
  pitchHop: number;
  /** `[timeS, f0Hz (0 = unvoiced), confidence]` per frame. */
  pitchTrack: [number, number, number][];
  spectral: {
    centroidHz: number;
    rolloffHz: number;
    flatness: number;
    hop: number;
    /** `[timeS, centroidHz, rolloffHz, flatness, levelDb]`. */
    track: [number, number, number, number, number][];
  };
  bands: { name: string; loHz: number; hiHz: number; db: number; share: number }[];
  stereo: { correlation: number; width: number; balanceDb: number };
  envelope: { peakTime: number; attack: number; decay20: number | null; decay40: number | null };
  voice: { voicedRatio: number; f1: number | null; f2: number | null; f3: number | null } | null;
}

/** `prism_sound::analysis::Comparison` (a vs b). */
export interface SoundComparison {
  similarity: number;
  timbreSimilarity: number;
  meanDistanceDb: number;
  durationA: number;
  durationB: number;
  durationDiff: number;
  loudnessDiff: number;
  peakDiffDb: number;
  pitchDiffCents: number | null;
  centroidAHz: number;
  centroidBHz: number;
  brightnessDiffPercent: number;
  flatnessDiff: number;
  onsetsA: number;
  onsetsB: number;
  tempoA: number | null;
  tempoB: number | null;
  bands: { name: string; aDb: number; bDb: number; diffDb: number }[];
  summary: string;
}

/** Result of {@link SoundRendererApi.render}. */
export interface SoundRenderResult {
  left: Float32Array;
  right: Float32Array;
  rate: number;
  warnings: string[];
  analysis: SoundAnalysis;
  /** The document's resolved `export` settings (path includes the variant suffix). */
  export: { path: string; bitDepth: number; channels: number; normalize?: number | null; trim?: boolean; tail?: number | null; loop?: { start: number; end: number; crossfade: number } | null };
}

/** The sound renderer (`SoundRenderer` exported by prism_wasm.js). */
export interface SoundRendererApi {
  /**
   * Registers a file: project files by project path (`samples/kick.wav`),
   * shared-cache files as `cache/<cache path>` (`cache/soundfonts/GeneralUser-GS.sf2`,
   * `cache/voices/<id>/<id>.onnx`), fetched from `/api/sound/cache/file`.
   * Un-prefixed `soundfonts/…`, `voices/…`, `dicts/…` paths count as both.
   */
  addFile(path: string, bytes: Uint8Array): void;
  hasFile(path: string): boolean;
  /** Renders a `*.sound.json` (text + project path). Throws the error message on failure. */
  render(json: string, path: string, variant?: string): SoundRenderResult;
  analyze(left: Float32Array, right: Float32Array, rate: number): SoundAnalysis;
  /** Plain-text measurement summary. */
  summary(left: Float32Array, right: Float32Array, rate: number): string;
  /** PNG bytes. */
  spectrogram(left: Float32Array, right: Float32Array, rate: number, width: number, height: number): Uint8Array;
  /** PNG bytes. */
  waveform(left: Float32Array, right: Float32Array, rate: number, width: number, height: number): Uint8Array;
  /** WAV bytes; `exportJson` = the document's `export` object as JSON (`""` = 24-bit stereo). */
  encodeWav(left: Float32Array, right: Float32Array, rate: number, exportJson: string): Uint8Array;
  decodeWav(bytes: Uint8Array): { left: Float32Array; right: Float32Array; rate: number };
  compare(
    aLeft: Float32Array,
    aRight: Float32Array,
    aRate: number,
    bLeft: Float32Array,
    bRight: Float32Array,
    bRate: number,
  ): SoundComparison;
  free(): void;
}

/** Shape of the prism_wasm.js ES module. */
export interface PrismWasmModule {
  default: (input?: unknown) => Promise<unknown>;
  PrismView: {
    create(canvas: HTMLCanvasElement | OffscreenCanvas, mode: PrismMode, host: PrismHost): Promise<PrismViewApi>;
  };
  /** Optional so older bundles still load (the UI editor then asks for a rebuild). */
  UiLayout?: new () => UiLayoutApi;
  /** Optional so older bundles still load (the Sound tab then asks for a rebuild). */
  SoundRenderer?: new () => SoundRendererApi;
  RigEditor?: new () => RigEditorApi;
  AnimatorPreview?: new () => AnimatorPreviewApi;
  /** Shape-key capture (Model tab): blend shape JSON reproducing a part's edit. */
  shapeFromEdit?: (before: string, after: string, path: string, name: string) => string;
}

export interface RigEditorApi {
  addFile(path: string, bytes: Uint8Array): void;
  loadModel(json: string): void;
  loadGlb(bytes: Uint8Array): void;
  bind(json: string): void;
  geometry(): string;
  initialRig(): string;
  sample(clip: string, time: number): string;
  exportGlb(): Uint8Array;
  free(): void;
}

// ---- Animator window live preview (prism_wasm/src/animator_preview.rs) ----
/** Controller-driven model preview. JSON-returning calls give an `AnimatorPreviewFrame`. */
export interface AnimatorPreviewApi {
  addFile(path: string, bytes: Uint8Array): void;
  hasFile(path: string): boolean;
  /** `rig`: the model's `.rig.json` sidecar text ('' = none). */
  loadModelGlb(bytes: Uint8Array, rig: string): void;
  loadModelDoc(json: string, rig: string): void;
  /** Returns the compile problems (JSON string array). */
  setController(json: string): string;
  /** Project GLBs the clips reference that are not loaded yet (JSON string array). */
  requiredFiles(): string;
  /** `value`: JSON number or boolean. */
  setParam(name: string, value: string): void;
  fireTrigger(name: string): void;
  resetParams(): void;
  params(): string;
  /** `layer` -1 = the first layer that has the state. */
  play(state: string, layer: number): boolean;
  crossfade(state: string, layer: number, duration: number): boolean;
  restart(): void;
  update(dt: number): string;
  seek(time: number): string;
  previewState(layer: number, state: string, normalizedTime: number): string;
  previewTransition(layer: number, from: string, fromTime: number, to: string, toTime: number, weight: number): string;
  /** `{length, speed, loop}` JSON or "null". */
  stateTiming(layer: number, state: string): string;
  geometry(): string;
  /** Posed vertex positions of every primitive, concatenated xyz. */
  positions(): Float32Array;
  frame(): string;
  free(): void;
}
export interface AnimatorPreviewGeometry {
  meshes: { node: string; indices: number[]; vertices: number; color: number[] }[];
  joints: { name: string; parent: number | null }[];
  skinned: boolean;
  clips: { name: string; duration: number }[];
}
export interface AnimatorPreviewFrame {
  /** Overlay joint positions, flat xyz (order of `geometry().joints`). */
  joints: number[];
  layers: { name: string; state: string; normalizedTime: number; inTransition: boolean; nextState: string; transitionProgress: number; weight: number }[];
  params: Record<string, number | boolean>;
  shown: { kind: 'live'; time: number } | { kind: 'state'; layer: number; state: string; time: number } | { kind: 'transition'; layer: number; from: string; to: string; weight: number };
  errors: string[];
}
// ---- end Animator preview ----

export type PrismEvent =
  | { type: 'ready' }
  | { type: 'selection'; ids: string[] }
  | { type: 'sceneChanged'; path: string; doc: unknown }
  /** Scene view state: layout, view axes (yaw/pitch/ortho), pivot, selected-camera preview, view mode. */
  | { type: 'editorView'; layout: string; active: number; views: unknown[]; pivot: string; preview: unknown }
  | { type: 'terrainChanged'; path: string; text: string }
  /** End of a tree-painting stroke: trees to add / remove (world space; see terrain-tools.ts). */
  | { type: 'terrainTrees'; terrain: string; group: string | null; add: unknown[]; remove: string[] }
  /** A script's `terrain_save()` (Play view): the editor writes the terrain data file. */
  | { type: 'terrainSaved'; path: string; text: string }
  /** Play stopped with unsaved play-mode terrain changes (kept only if the user chooses to). */
  | { type: 'terrainPlayChanged'; path: string; text: string; edits: number }
  /** `reload`: answers a document the editor supplied (true) vs an engine edit (false); absent in older bundles. */
  | { type: 'modelChanged'; path: string; doc: unknown; replace?: boolean; reload?: boolean }
  | { type: 'modelGizmo'; mode: 'translate' | 'rotate' | 'scale' | 'none'; space: 'world' | 'local' }
  | {
      type: 'meshEdit';
      active: boolean;
      path?: string;
      mode?: 'vertex' | 'edge' | 'face';
      faces?: boolean;
      selected?: number;
      stats?: unknown;
      modal?: unknown;
      last?: unknown;
      status?: string | null;
      options?: unknown;
    }
  | { type: 'meshLabels'; labels: unknown }
  /** Answer to the `uvLayout` modelOp: a part's UVs, islands and distortion (state/uv.ts parses it). */
  | { type: 'uvLayout'; path: string; [field: string]: unknown }
  | { type: 'sculpt'; active: boolean; path?: string; brush?: unknown }
  /** Texture paint state, saved PNGs and bake progress / results (state/paint.ts). */
  | { type: 'paint'; active: boolean; [field: string]: unknown }
  | { type: 'paintSaved'; material: string; [field: string]: unknown }
  | { type: 'bake'; state: 'running' | 'done' | 'error' | 'cancelled'; progress?: number; [field: string]: unknown }
  /** Answer to the `remeshPreview` modelOp: mesh counts before / after a remesh or decimate (nothing changed). */
  | {
      type: 'remeshStats';
      path: string;
      kind: 'remesh' | 'decimate';
      before: { vertices: number; faces: number; triangles: number; quads: number };
      after: { vertices: number; faces: number; triangles: number; quads: number };
      /** `after` is an estimate (job too heavy to run for a live preview). */
      estimated?: boolean;
      ms: number;
    }
  | { type: 'log'; level: 'debug' | 'info' | 'warn' | 'error'; source: string; message: string }
  | { type: 'stats'; fps: number; drawCalls: number; triangles: number }
  | { type: 'error'; message: string }
  /** Sequence director: state changes, the time while previewing, fired events, the end. */
  | { type: 'sequenceState'; [field: string]: unknown }
  | { type: 'sequenceTime'; time: number }
  | { type: 'sequenceEvent'; sequence?: string; time: number; name: string; data?: unknown; target?: string }
  | { type: 'sequenceFinished'; [field: string]: unknown }
  | { type: 'playState'; state: 'playing' | 'paused' | 'stopped' }
  /** Scripts' `set_cursor_visible` (applied to the canvas by the Viewport). */
  | { type: 'cursorVisible'; visible: boolean }
  /** Play mode: no camera entity renders the game (the Viewport shows a message). */
  | { type: 'noCamera'; active: boolean }
  /** Editor play speed changed. */
  | { type: 'playSpeed'; speed: number }
  /** A paused game advanced one frame. */
  | { type: 'playStep'; frame: number }
  /** Additive scenes loaded next to the open scene. */
  | { type: 'additiveScenes'; scenes: string[]; errors: string[] }
  /** Procedural generator hosts (Runtime::procgen_status) and spawns. */
  | { type: 'procgenStatus'; [field: string]: unknown }
  | { type: 'procgenSpawned'; [field: string]: unknown }
  /** Edit-mode particle preview state (`setOption("particlePreview")`, components/particles/preview-store.ts). */
  | {
      type: 'particlePreview';
      time: number;
      speed: number;
      paused: boolean;
      stopped: boolean;
      particles: number;
      systems: { id: string; count: number; playing: boolean; time: number; duration: number; looping: boolean }[];
    };

export const PRISM_BASE_URL = `${BASE_PATH}/engine/`;
export const PRISM_SCRIPT_URL = `${PRISM_BASE_URL}prism_wasm.js`;
export const ENGINE_MISSING_HINT = 'Engine runtime not built — run pnpm engine:build';

export type EngineStatus =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'ready' }
  | { state: 'missing'; reason: string }
  | { state: 'error'; message: string };

export const useEngineStatus = create<{ status: EngineStatus }>(() => ({
  status: { state: 'idle' },
}));

function setStatus(status: EngineStatus): void {
  useEngineStatus.setState({ status });
}

let modulePromise: Promise<PrismWasmModule | null> | null = null;

/**
 * The last Rust panic message the engine printed (console_error_panic_hook
 * writes it to console.error just before the WebAssembly trap), so a crashed
 * view can report what actually went wrong instead of "unreachable".
 */
let lastPanic: string | null = null;
/** The last uncaught engine trap (a crash inside the engine's own frame loop). */
let lastTrap: string | null = null;
/** The engine's linear memory, for the crash report (browsers cap it at 4 GB). */
let wasmMemory: WebAssembly.Memory | null = null;

/** " (engine memory 3912 MB ...)" for crash reports. */
function memoryNote(): string {
  const bytes = wasmMemory?.buffer.byteLength ?? 0;
  if (!bytes) return '';
  const mb = Math.round(bytes / 1048576);
  return mb > 3800 ? ` (engine memory ${mb} MB: at the 4 GB browser limit, so this is most likely out of memory)` : ` (engine memory ${mb} MB)`;
}

/** A "RefCell already borrowed" panic follows an earlier crash that left the engine's state locked. */
function isFollowOn(message: string): boolean {
  return /RefCell already/.test(message);
}

function watchPanics(): void {
  const original = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    const text = args.map(String).join(' ');
    const at = text.indexOf('panicked at');
    if (at >= 0) {
      const message = (text.slice(at, at + 600).split('Stack:')[0] ?? '').replace(/\s+/g, ' ').trim();
      // Keep the panic that started it, not the follow-on lock errors.
      if (!(lastPanic && !isFollowOn(lastPanic) && isFollowOn(message))) lastPanic = message;
    }
    original(...args);
  };
  // The engine runs its frames from its own requestAnimationFrame callback:
  // a trap there never reaches EngineView.fail, only the window.
  if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
      const error: unknown = event.error;
      if (typeof WebAssembly !== 'undefined' && error instanceof WebAssembly.RuntimeError) {
        lastTrap = `${error.message}${memoryNote()}`;
      }
    });
  }
}

/** What crashed the engine: the first real panic, else the trap, else the follow-on. */
function crashCause(): string | null {
  if (lastPanic && !isFollowOn(lastPanic)) return `${lastPanic}${memoryNote()}`;
  if (lastTrap) return `${lastTrap}${lastPanic ? ` (then: ${lastPanic})` : ''}`;
  return lastPanic ? `${lastPanic}${memoryNote()}` : null;
}

/** A WebAssembly trap: the engine panicked; this view's state is gone. */
function isEngineTrap(error: unknown): boolean {
  if (typeof WebAssembly !== 'undefined' && error instanceof WebAssembly.RuntimeError) return true;
  const message = error instanceof Error ? error.message : String(error);
  return /unreachable|RefCell already/.test(message);
}

/**
 * Load and initialise the WASM module once. Resolves to null (and records why)
 * when the bundle is not there, so callers fall back to the placeholder.
 */
export function loadPrism(): Promise<PrismWasmModule | null> {
  if (modulePromise) return modulePromise;
  setStatus({ state: 'loading' });
  modulePromise = (async () => {
    try {
      // Probe first: a missing bundle would otherwise surface as an opaque
      // module-load failure (or the SPA fallback's HTML parsed as JS).
      const probe = await fetch(PRISM_SCRIPT_URL, { method: 'GET', credentials: 'same-origin' });
      const type = probe.headers.get('content-type') ?? '';
      if (!probe.ok || !/javascript|ecmascript/i.test(type)) {
        setStatus({ state: 'missing', reason: ENGINE_MISSING_HINT });
        return null;
      }
      const mod = (await import(/* @vite-ignore */ PRISM_SCRIPT_URL)) as PrismWasmModule;
      if (typeof mod.default !== 'function' || !mod.PrismView?.create) {
        setStatus({
          state: 'error',
          message: 'prism_wasm.js does not export init() and PrismView.',
        });
        return null;
      }
      watchPanics();
      const exports = (await mod.default()) as { memory?: WebAssembly.Memory } | undefined;
      wasmMemory = exports?.memory ?? null;
      // Diagnostics (DevTools): megabytes of engine memory in this window.
      (globalThis as { prismMemoryMb?: () => number }).prismMemoryMb = () =>
        Math.round((wasmMemory?.buffer.byteLength ?? 0) / 1048576);
      setStatus({ state: 'ready' });
      return mod;
    } catch (error) {
      setStatus({
        state: 'error',
        message: `Engine failed to load: ${error instanceof Error ? error.message : String(error)}`,
      });
      return null;
    }
  })();
  return modulePromise;
}

/** Forget a failed load so the next viewport retries (e.g. after engine:build). */
export function retryPrism(): void {
  // A crashed or failed engine worker is replaced on the next view.
  resetEngineWorker();
  const status = useEngineStatus.getState().status;
  if (status.state === 'missing' || status.state === 'error') {
    modulePromise = null;
    setStatus({ state: 'idle' });
  }
}

export function parsePrismEvent(json: string): PrismEvent | null {
  try {
    const value = JSON.parse(json) as { type?: unknown };
    if (!value || typeof value !== 'object' || typeof value.type !== 'string') return null;
    return value as PrismEvent;
  } catch {
    return null;
  }
}

/** Where an asset dropped on the scene view lands (`PrismView.dropPoint`). */
export interface DropPoint {
  /** Entity under the cursor (meshes, terrain, sprites). */
  id: string | null;
  point: [number, number, number];
  /** On a surface rather than the ground plane / in front of the camera. */
  surface: boolean;
}

export interface CommandOutcome {
  ok: boolean;
  error?: string;
}

/**
 * A live engine view with defensive wrappers: every call is a no-op after
 * `dispose()`, and engine exceptions are turned into results rather than
 * crashing the React tree.
 *
 * The view usually runs in the engine worker (engine-bridge.ts), so every
 * call that returns something is asynchronous; calls without a result are
 * sent and return at once. Calls reach the engine in the order they are made.
 */
export class EngineView {
  private disposed = false;
  private pending: Promise<void> = Promise.resolve();
  private failure: string | null = null;
  /** The engine panicked: further calls would only fail again, so they are skipped. */
  private crashed = false;
  /** Called once when the engine crashes (the Viewport offers a restart). */
  onCrash: ((message: string) => void) | null = null;
  /** Worker views: the page's audio output (an AudioWorklet fed by the worker). */
  private audio: { ctx: AudioContext; node: AudioWorkletNode } | null = null;
  /** Poll calls (trace, profiler, live tree) still waiting on the engine. */
  private inflight = new Map<string, Promise<unknown>>();
  /** Last size / visibility sent (diagnostics: `prismEngineViews()` in DevTools). */
  private shown: { visible: boolean; size: [number, number, number] | null } = { visible: true, size: null };

  /** Reports a failed call; a crash is reported once, with the panic message. */
  private fail(label: string, error: unknown): void {
    if (this.crashed) return;
    if (!this.channel.remote && isEngineTrap(error)) {
      const cause = crashCause();
      this.markCrashed(`The ${this.mode} engine crashed${cause ? `: ${cause}` : ` (${label})${memoryNote()}`}`);
      return;
    }
    this.report(`${label}: ${error instanceof Error ? error.message : String(error)}`);
  }

  private markCrashed(message: string): void {
    if (this.crashed) return;
    this.crashed = true;
    this.failure = message;
    this.report(message);
    this.onCrash?.(message);
  }

  get isCrashed(): boolean {
    return this.crashed;
  }

  constructor(
    readonly mode: PrismMode,
    private readonly channel: ViewChannel,
    private readonly report: (message: string) => void,
  ) {
    channel.onCrash = (message) => this.markCrashed(`The ${this.mode} engine crashed: ${message}`);
  }

  get alive(): boolean {
    return !this.disposed;
  }

  get error(): string | null {
    return this.failure;
  }

  /** The view runs in the engine worker (off the editor's thread). */
  get inWorker(): boolean {
    return this.channel.remote;
  }

  private has(method: string): boolean {
    return this.channel.methods.has(method);
  }

  /** Whether this engine build has a PrismView method (features detected by name). */
  hasMethod(name: string): boolean {
    return this.has(name);
  }

  /** Calls a PrismView method this wrapper has no typed binding for; rejects when it fails. */
  callMethod<T>(name: string, args: unknown[] = []): Promise<T> {
    if (this.disposed || this.crashed) return Promise.reject(new Error(this.failure ?? 'view disposed'));
    return this.channel.call<T>(name, args);
  }

  /** Fire-and-forget call. */
  private send(label: string, method: string, args: unknown[] = []): void {
    if (this.disposed || this.crashed) return;
    try {
      this.channel.send(method, args);
    } catch (error) {
      this.fail(label, error);
    }
  }

  /** Call with a result; `fallback` when disposed, crashed or failed. */
  private async ask<T>(label: string, method: string, args: unknown[], fallback: T): Promise<T> {
    if (this.disposed || this.crashed) return fallback;
    try {
      return await this.channel.call<T>(method, args);
    } catch (error) {
      if (!this.disposed) this.fail(label, error);
      return fallback;
    }
  }

  /** Like {@link ask}, but a poll still waiting on the engine is shared, not sent again. */
  private poll<T>(key: string, run: () => Promise<T>): Promise<T> {
    const waiting = this.inflight.get(key);
    if (waiting) return waiting as Promise<T>;
    const p = run().finally(() => this.inflight.delete(key));
    this.inflight.set(key, p);
    return p;
  }

  private guardAsync(label: string, run: () => Promise<unknown>): Promise<void> {
    // Serialize reads/reloads so an older network response cannot replace a
    // newer document. Recheck disposal when queued work actually begins.
    this.pending = this.pending.then(async () => {
      if (this.disposed || this.crashed) return;
      try {
        await run();
      } catch (error) {
        if (!this.disposed) {
          this.fail(label, error);
          if (!this.crashed) this.failure = `${label}: ${error instanceof Error ? error.message : String(error)}`;
        }
      }
    });
    return this.pending;
  }

  private static parseJson<T>(raw: unknown): T | null {
    if (typeof raw !== 'string' || !raw || raw === 'null') return null;
    return JSON.parse(raw) as T;
  }

  private outcome(label: string, method: string, command: object): Promise<CommandOutcome> {
    return this.ask<string | null>(label, method, [JSON.stringify(command)], null).then((raw) => {
      if (raw === null) return { ok: false, error: this.disposed ? 'view disposed' : (this.failure ?? `${label} failed`) };
      try {
        return JSON.parse(raw) as CommandOutcome;
      } catch {
        return { ok: false, error: `bad command result: ${raw}` };
      }
    });
  }

  openProject(): Promise<void> {
    return this.guardAsync('openProject', () => this.channel.call('openProject'));
  }
  openScene(path: string): Promise<void> {
    return this.guardAsync(`openScene ${path}`, () => this.channel.call('openScene', [path]));
  }
  openModel(path: string): Promise<void> {
    return this.guardAsync(`openModel ${path}`, () => this.channel.call('openModel', [path]));
  }
  modelCommand(command: object): Promise<CommandOutcome> {
    return this.outcome('modelCommand', 'modelCommand', command);
  }
  command(command: object): Promise<CommandOutcome> {
    return this.outcome('command', 'command', command);
  }
  /** Drop placement under CSS pixel (x, y); null when the bundle predates it. */
  async dropPoint(x: number, y: number): Promise<DropPoint | null> {
    if (!this.has('dropPoint')) return null;
    return EngineView.parseJson<DropPoint>(await this.ask('dropPoint', 'dropPoint', [x, y], null));
  }
  setTool(tool: PrismTool): void {
    this.send('setTool', 'setTool', [tool]);
  }
  setOption(key: string, value: string): void {
    this.send(`setOption ${key}`, 'setOption', [key, value]);
  }
  play(): Promise<void> {
    return this.guardAsync('play', async () => {
      await this.ensureAudio();
      await this.channel.call('play');
    });
  }
  stop(): void {
    this.send('stop', 'stop');
  }
  pause(paused: boolean): void {
    this.send('pause', 'pause', [paused]);
  }
  /** The Play tab's phone / tablet preview: the mouse acts as a finger and touch controls show. */
  /** The Prism account of the person playing, or null (games with `settings.account` see it). */
  setAccount(account: { id: number; username: string; token?: string } | null): void {
    if (this.has('setAccount')) this.send('account', 'setAccount', [account ? JSON.stringify(account) : '']);
  }

  setTouchEmulation(on: boolean): void {
    if (this.has('setTouchEmulation')) this.send('touch emulation', 'setTouchEmulation', [on]);
  }
  /** The screen's safe area (notches, rounded corners, home bar) in CSS px: touch controls stay inside it. */
  setSafeAreaInsets(top: number, right: number, bottom: number, left: number): void {
    if (this.has('setSafeAreaInsets')) this.send('safe area', 'setSafeAreaInsets', [top, right, bottom, left]);
  }
  fileChanged(path: string): Promise<void> {
    return this.guardAsync(`fileChanged ${path}`, () =>
      this.channel.call('fileChanged', [path]).catch((error: unknown) => {
        // A rename or delete also reports the old path: nothing left to reload.
        const message = error instanceof Error ? error.message : String(error);
        if (!message.includes(`${path.replace(/\\/g, '/').replace(/^\.?\/+/, '')}: not found`)) throw error;
      }),
    );
  }
  resize(width: number, height: number, dpr: number): void {
    if (width <= 0 || height <= 0) return;
    this.shown.size = [width, height, dpr];
    this.send('resize', 'resize', [width, height, dpr]);
  }
  /** Worker views: whether the page shows the view (hidden views skip rendering). */
  setVisible(visible: boolean): void {
    this.shown.visible = visible;
    if (this.has('setVisible')) this.send('setVisible', 'setVisible', [visible]);
  }
  /** Worker views: the pads from the page's Gamepad API (see Viewport). */
  setGamepads(json: string): void {
    if (this.has('setGamepads')) this.send('setGamepads', 'setGamepads', [json]);
  }
  input(event: object): void {
    if (!this.has('input')) return;
    this.send('input', 'input', [JSON.stringify(event)]);
  }
  get acceptsInput(): boolean {
    return this.has('input');
  }
  /** The view as PNG bytes (null when unavailable). */
  screenshot(): Promise<Uint8Array | null> {
    const method = this.has('screenshotPng') ? 'screenshotPng' : 'screenshot';
    return this.ask<Uint8Array | null>('screenshot', method, [], null);
  }
  /** Play tools are in this bundle. */
  get hasPlayTools(): boolean {
    return this.has('liveTree');
  }
  step(): Promise<boolean> {
    return this.has('step') ? this.ask('step', 'step', [], false) : Promise.resolve(false);
  }
  setPlaySpeed(speed: number): void {
    if (this.has('setPlaySpeed')) this.send('setPlaySpeed', 'setPlaySpeed', [speed]);
  }
  liveTree(): Promise<LiveTree | null> {
    if (!this.has('liveTree')) return Promise.resolve(null);
    return this.poll('liveTree', async () => EngineView.parseJson<LiveTree>(await this.ask('liveTree', 'liveTree', [], null)));
  }
  async liveEntity(id: string): Promise<LiveEntity | null> {
    if (!this.has('liveEntity')) return null;
    return EngineView.parseJson<LiveEntity>(await this.ask('liveEntity', 'liveEntity', [id], null));
  }
  async liveEdit(edit: LiveEdit): Promise<CommandOutcome> {
    if (!this.has('liveEdit')) return { ok: false, error: 'this engine build has no live editing (pnpm engine:build)' };
    return this.outcome('liveEdit', 'liveEdit', edit);
  }
  setAdditiveScenes(paths: readonly string[]): Promise<void> {
    if (!this.has('setAdditiveScenes')) return Promise.resolve();
    return this.guardAsync('setAdditiveScenes', () => this.channel.call('setAdditiveScenes', [JSON.stringify(paths)]));
  }
  /** The bundle has the sequence director (cutscene preview). */
  get supportsSequences(): boolean {
    return this.has('sequenceCommand');
  }
  /** Sends a director command; resolves with the new state (null when unsupported or failed). */
  async sequenceCommand(command: SequenceCommand): Promise<SequenceState | null> {
    if (!this.has('sequenceCommand')) return null;
    return EngineView.parseJson<SequenceState>(await this.ask(`sequenceCommand ${command.cmd}`, 'sequenceCommand', [JSON.stringify(command)], null));
  }
  async sequenceState(): Promise<SequenceState | null> {
    if (!this.has('sequenceState')) return null;
    return EngineView.parseJson<SequenceState>(await this.ask('sequenceState', 'sequenceState', [], null));
  }
  /** The bundle has the game trace (Game console, assistant read_game_trace). */
  get supportsTrace(): boolean {
    return this.has('traceTake');
  }
  get supportsWorldSnapshot(): boolean {
    return this.has('worldSnapshot');
  }
  /** Applies a trace config; false when unsupported (it is sent, not awaited). */
  traceConfig(config: TraceConfig): boolean {
    if (!this.has('traceConfig') || this.disposed || this.crashed) return false;
    this.send('traceConfig', 'traceConfig', [JSON.stringify(config)]);
    return true;
  }
  private traceFailed = false;
  /**
   * Trace events after `sinceSeq` (null when unsupported or disposed). Polled
   * every 250 ms, so a failure is reported once, not on every poll.
   */
  traceTake(sinceSeq: number, filter: TraceFilter = {}): Promise<TraceEvent[] | null> {
    if (this.disposed || this.crashed || !this.has('traceTake')) return Promise.resolve(null);
    return this.poll('traceTake', async () => {
      try {
        const events = parseTraceArray(await this.channel.call<string>('traceTake', [sinceSeq, JSON.stringify(filter)]));
        this.traceFailed = false;
        return events;
      } catch (error) {
        if (!this.traceFailed && !this.disposed) this.fail('traceTake', error);
        this.traceFailed = true;
        return null;
      }
    });
  }
  async worldSnapshot(filter: SnapshotFilter = {}): Promise<WorldSnapshot | null> {
    if (!this.has('worldSnapshot')) return null;
    return EngineView.parseJson<WorldSnapshot>(await this.ask('worldSnapshot', 'worldSnapshot', [JSON.stringify(filter)], null));
  }
  async entityState(id: string): Promise<SnapshotEntity | null> {
    if (!this.has('entityState')) return null;
    return EngineView.parseJson<SnapshotEntity>(await this.ask('entityState', 'entityState', [id], null));
  }
  /** The bundle has the Profiler / Frame Debugger bindings. */
  get supportsProfiler(): boolean {
    return this.has('profiler') && this.has('profilerFrames');
  }
  get supportsFrameDebugger(): boolean {
    return this.has('frameDebugger');
  }
  /** Profiler command → status (null when unsupported / failed). */
  async profiler(command: ProfilerCommand): Promise<ProfilerStatus | null> {
    if (!this.has('profiler')) return null;
    const raw = await this.ask<string | null>('profiler', 'profiler', [JSON.stringify(command)], null);
    if (raw === null) return null;
    try {
      const out = JSON.parse(raw) as { ok: boolean; status?: ProfilerStatus; error?: string };
      if (!out.ok) throw new Error(out.error ?? 'profiler command failed');
      return out.status ?? null;
    } catch (error) {
      this.fail('profiler', error);
      return null;
    }
  }
  /** Recorded frames with seq > `since` (-1 = every frame held). */
  profilerFrames(since: number, max?: number): Promise<ProfilerFramesResult | null> {
    if (!this.has('profilerFrames')) return Promise.resolve(null);
    return this.poll(`profilerFrames:${since}:${max ?? ''}`, async () =>
      EngineView.parseJson<ProfilerFramesResult>(await this.ask('profilerFrames', 'profilerFrames', max === undefined ? [since] : [since, max], null)),
    );
  }
  /** Frame debugger op; resolves to the parsed result (`{ok: false}` when unsupported). */
  async frameDebugger<T extends FrameDebuggerCapture | FrameDebuggerPreview>(command: FrameDebuggerCommand): Promise<T> {
    if (this.disposed || !this.has('frameDebugger')) return { ok: false, error: 'Frame debugger needs a newer engine build' } as T;
    try {
      return JSON.parse(await this.channel.call<string>('frameDebugger', [JSON.stringify(command)])) as T;
    } catch (error) {
      return { ok: false, error: error instanceof Error ? error.message : String(error) } as T;
    }
  }

  /**
   * Worker views have no AudioContext in the worker: the page creates one
   * with an AudioWorklet and hands its port to the engine, which streams the
   * mixed sound into it (prism_wasm/src/worklet_audio.rs). Page views keep
   * using WebAudio directly.
   */
  private async ensureAudio(): Promise<void> {
    if (!this.channel.remote || this.audio || !this.has('attachAudio') || typeof AudioWorkletNode === 'undefined') return;
    try {
      const ctx = new AudioContext({ latencyHint: 'interactive' });
      await ctx.audioWorklet.addModule(ENGINE_AUDIO_WORKLET_URL);
      const node = new AudioWorkletNode(ctx, 'prism-engine-output', { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [2] });
      node.connect(ctx.destination);
      this.audio = { ctx, node };
      this.channel.send('attachAudio', [node.port, ctx.sampleRate], [node.port]);
      void ctx.resume().catch(() => undefined);
    } catch (error) {
      this.report(`audio output unavailable, playing without sound: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  /** Browsers start audio suspended until a user gesture: the Viewport calls this on input. */
  resumeAudio(): void {
    if (this.audio?.ctx.state === 'suspended') void this.audio.ctx.resume().catch(() => undefined);
  }

  /** State for diagnostics (`prismEngineViews()` in DevTools). */
  describe(): Record<string, unknown> {
    return {
      mode: this.mode,
      inWorker: this.channel.remote,
      alive: !this.disposed,
      crashed: this.crashed,
      error: this.failure,
      ...this.shown,
      audio: this.audio?.ctx.state ?? null,
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.channel.free();
    if (this.audio) {
      this.audio.node.disconnect();
      void this.audio.ctx.close().catch(() => undefined);
      this.audio = null;
    }
  }
}

/** Create a view, or null when the engine is unavailable. */
export async function createEngineView(
  canvas: HTMLCanvasElement,
  mode: PrismMode,
  host: PrismHost,
  report: (message: string) => void,
): Promise<EngineView | null> {
  if (workerPossible()) {
    if (useEngineStatus.getState().status.state !== 'ready') setStatus({ state: 'loading' });
    const start = await startEngineWorker(PRISM_SCRIPT_URL);
    if (start.missing) {
      setStatus({ state: 'missing', reason: start.error ?? ENGINE_MISSING_HINT });
      return null;
    }
    if (start.ok && start.caps?.gpu && start.caps.raf && start.caps.offscreen) {
      setStatus({ state: 'ready' });
      try {
        return new EngineView(mode, await workerChannel(canvas, mode, host), report);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        report(`PrismView.create(${mode}) failed: ${message}`);
        setStatus({ state: 'error', message: `Could not start the ${mode} view: ${message}` });
        return null;
      }
    }
    report(`the engine worker cannot render (${start.error ?? `WebGPU ${start.caps?.gpu ? 'ok' : 'missing'}, animation frames ${start.caps?.raf ? 'ok' : 'missing'}`}); running the engine on the page`);
  }
  const mod = await loadPrism();
  if (!mod) return null;
  try {
    const api = await mod.PrismView.create(canvas, mode, host);
    return new EngineView(mode, pageChannel(api), report);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    report(`PrismView.create(${mode}) failed: ${message}`);
    setStatus({ state: 'error', message: `Could not start the ${mode} view: ${message}` });
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* View registry: lets the document stores forward edits to live views. */

const views: Partial<Record<PrismMode, EngineView>> = {};

export function registerView(mode: PrismMode, view: EngineView | null): void {
  if (view) views[mode] = view;
  else delete views[mode];
}

// Diagnostics (DevTools): every live engine view of this page.
(globalThis as { prismEngineViews?: () => unknown[] }).prismEngineViews = () => Object.values(views).map((v) => v.describe());

export function getView(mode: PrismMode): EngineView | null {
  const view = views[mode];
  return view && view.alive ? view : null;
}
